import React from 'react'
import MicroMetaFooter from '../../components/micro/MicroMetaFooter'
import MagazineHtml from '../../components/renderers/MagazineHtml'

type Props = {
  microText: string
  categoryLabel?: string | null
  authorName?: string | null
  authorHandle?: string | null
  authorAvatarUrl?: string | null
  createdAt?: string | null
  viewsCount?: number | null
  hasProof?: boolean | null
  receiptStatus?: 'pending' | 'confirmed' | 'failed' | string | null
  html?: string
  variant?: 'full' | 'compact'
}

function initialsFrom(name: string): string {
  const s = String(name || '').trim()
  if (!s) return '?'
  const parts = s.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export default function MicroReaderPreview({
  microText,
  categoryLabel,
  authorName,
  authorHandle,
  authorAvatarUrl,
  createdAt,
  viewsCount,
  hasProof,
  receiptStatus,
  html,
  variant = 'full',
}: Props) {
  const handle = String(authorHandle || '').trim()
  const handleLabel = handle ? (handle.startsWith('@') ? handle : `@${handle}`) : ''
  const nameLabel = String(authorName || '').trim()
  const categoryUpper = String(categoryLabel || '').trim().toUpperCase()
  const safeHtml = String(html || '').trim()
  const plainMicro = String(microText || '').trim()
  const compact = variant === 'compact'

  return (
    <div className="rounded-2xl border border-white/10 bg-[#07090f] overflow-hidden">
      <div className={compact ? 'px-4 pt-4 pb-3 border-b border-white/10' : 'px-5 pt-5 pb-4 border-b border-white/10'}>
        <div className="text-[11px] tracking-[0.18em] text-white/65 font-semibold">PIXE • MICRO</div>
        {categoryUpper ? <div className="mt-1 text-[10px] tracking-[0.16em] text-white/50">{categoryUpper}</div> : null}
        <div className={compact ? 'mt-2 text-xl leading-tight font-extrabold text-white whitespace-pre-line' : 'mt-3 text-2xl leading-tight font-extrabold text-white whitespace-pre-line'}>
          {plainMicro || 'Your micro text preview will show here...'}
        </div>

        <div className={compact ? 'mt-3 flex items-end justify-between gap-3' : 'mt-4 flex items-end justify-between gap-3'}>
          <div className="flex min-w-0 items-center gap-2">
            <div className="h-8 w-8 shrink-0 rounded-full border border-white/15 bg-black/30 overflow-hidden flex items-center justify-center">
              {authorAvatarUrl ? (
                <img src={authorAvatarUrl} alt={nameLabel || handleLabel || 'Author'} className="h-full w-full object-cover" />
              ) : (
                <div className="text-[10px] font-semibold text-white/85 tracking-[0.06em]">
                  {initialsFrom(nameLabel || handle || 'Me')}
                </div>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-white/85 truncate">{nameLabel || handleLabel || 'Me'}</div>
              {handleLabel ? <div className="text-[11px] text-white/60 truncate">{handleLabel}</div> : null}
            </div>
          </div>
          <MicroMetaFooter
            createdAt={createdAt}
            viewsCount={viewsCount ?? 0}
            hasProof={hasProof}
            receiptStatus={receiptStatus}
            compact
            showViewsLabel
          />
        </div>
      </div>

      {safeHtml ? (
        <div className={compact ? 'p-4' : 'p-5'}>
          <MagazineHtml surface="microPreview" html={safeHtml} className={compact ? 'pr-2' : ''} />
        </div>
      ) : (
        <div className={compact ? 'p-4 text-sm text-white/55' : 'p-5 text-sm text-white/55'}>
          Your reader page content will appear here.
        </div>
      )}
    </div>
  )
}
